import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Skeleton } from "@mui/material";
import { useApi } from "../../../../../../common/hooks";
import { isNonEmptyArray } from "../../../../../../common/utils";
import { CustomIconButton, CustomTooltip, icon } from "../../../../../../common/components";
import ComplianceChart from "./ComplianceChart";
import { AiDetectionsService } from "../../../../../../api";
import styles from "./DrawerContent.module.css";

import { setOriginalCounters } from "../../../slice/project-sheet-slice";
import { translate } from "../../../../../../common/providers";

interface Block {
	id: string;
	name: string;
	type: string;
	x: number;
	y: number;
	h: number;
	w: number;
}

interface Section {
	id?: string;
	uuid?: string;
	name: string;
	subsection?: Section[];
	layout?: Block[];
}

interface CounterResponse {
	compliantCount: number;
	nonCompliantCount: number;
	pendingCount: number;
	criteriaCount: number;
}

interface PercentageCounters {
	compliant: number;
	nonCompliant: number;
	pending: number;
}

interface RowProps {
	launchInProgress?: string | null;
	section: Section;
	selectedSection: string | null;
	onSelect: (section: Section | null) => void;
}

interface DrawerContentProps {
	selectedPageId: string;
	launchInProgress?: string | null;
	open: boolean;
	sections: Section[];
	selectedSection: string | null;
	onSelect: (section: Section | null) => void;
}

const Row: React.FC<RowProps> = ({ launchInProgress, section, selectedSection, onSelect }) => {
	const [open, setOpen] = useState(true);
	const hasSubsection = useMemo(() => isNonEmptyArray(section?.subsection), [section]);
	const hasCriteria = (part: Section | undefined): boolean =>
		isNonEmptyArray(part?.layout) ||
		(isNonEmptyArray(part?.subsection) && (part?.subsection || []).some((el) => isNonEmptyArray(el?.layout)));
	const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setOpen((prev) => !prev);
	};

	const rowRenderer = (information: Section, isParent: boolean, hasChildren: boolean) => (
		<div
			key={information?.id || information?.uuid}
			className={styles.container}
			data-is-child={!isParent}
			data-is-clickable={hasCriteria(information) && !launchInProgress}
			data-selected={information.uuid === selectedSection}
		>
			<div
				className={styles.content}
				data-has-children={hasChildren}
				data-is-clickable={hasCriteria(information) && !launchInProgress}
				role="presentation"
				onClick={() => {
					if (hasCriteria(information) && !launchInProgress) {
						onSelect(information);
					}
				}}
			>
				{!launchInProgress && hasChildren && (
					<CustomIconButton
						btnClassName={styles.button__btn}
						className={styles.button__wrapper}
						icon={(open && icon.faChevronDown) || icon.faChevronRight}
						iconClassName={styles.button__icon}
						size="xs"
						onClick={handleOpen}
					/>
				)}
				<CustomTooltip showWhenOverflow enterDelay={500} enterNextDelay={500} title={information.name}>
					<span className={styles.label}>{!launchInProgress && information.name}</span>
				</CustomTooltip>
			</div>
		</div>
	);
	return (
		<>
			{rowRenderer(section, true, hasSubsection)}
			{open && hasSubsection && section.subsection!.map((subsection) => rowRenderer(subsection, false, false))}
		</>
	);
};

export const DrawerContent: React.FC<DrawerContentProps> = ({
	selectedPageId,
	launchInProgress = null,
	open,
	sections,
	selectedSection,
	onSelect,
}) => {
	const dispatch = useDispatch();
	const [counters, setCounters] = useState<PercentageCounters>({} as PercentageCounters);
	const projectId = useSelector(({ context }: { context: { project?: { id?: string } } }) => context.project?.id);
	const originalCounters = useSelector(
		({ projectSheet }: { projectSheet: { originalCounters: CounterResponse } }) => projectSheet.originalCounters
	);
	const { call: getChartCounters } = useApi(AiDetectionsService.getChartCounters);

	const processCounters = useCallback((response: CounterResponse) => {
		const pending = response.pendingCount;
		const nonCompliant = response.nonCompliantCount;
		const compliant = response.compliantCount;
		const total = pending + nonCompliant + compliant;
		let percentageResponse: PercentageCounters = { compliant: 0, nonCompliant: 0, pending: 0 };
		if (total > 0) {
			percentageResponse = {
				compliant: Math.round((compliant / total) * 100),
				nonCompliant: Math.round((nonCompliant / total) * 100),
				pending: Math.round((pending / total) * 100),
			};
		}
		setCounters(percentageResponse);
	}, []);
	useEffect(() => {
		async function fetchCounters() {
			try {
				if (selectedPageId && projectId) {
					const response: CounterResponse = await getChartCounters({ projectId, pageId: selectedPageId });
					dispatch(setOriginalCounters(response));
				}
			} catch (err) {
				console.error(err);
			}
		}
		fetchCounters();
	}, [dispatch, getChartCounters, projectId, selectedPageId, processCounters]);
	useEffect(() => {
		processCounters(originalCounters);
	}, [originalCounters, processCounters]);
	const handleSelectAll = () => {
		onSelect(null);
	};
	return (
		<div className={styles.drawerContent}>
			{open && (
				<>
					<ComplianceChart
						data={{
							compliant: counters.compliant,
							nonCompliant: counters.nonCompliant,
							pending: counters.pending,
						}}
						isLoading={!!launchInProgress}
					/>
					{(launchInProgress && <Skeleton className={styles.allSelector__skeleton} />) || (
						<div
							data-is-clickable
							className={`${styles.container} ${styles.allSelector}`}
							data-selected={!selectedSection}
							role="presentation"
							onClick={handleSelectAll}
						>
							{translate("dashboards.project-sheet.criteria.list.all")}
						</div>
					)}
					{isNonEmptyArray(sections) &&
						sections.map((section) => (
							<Row
								key={section?.uuid || section?.id || section?.name}
								launchInProgress={launchInProgress}
								section={section}
								selectedSection={selectedSection}
								onSelect={onSelect}
							/>
						))}
				</>
			)}
		</div>
	);
};
